import * as THREE from 'three';
import React, { Component } from 'react';
import * as _ from 'lodash';
import { useLocation } from 'react-router-dom';
import { useSelector, useStore } from 'react-redux';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';

import { transformSVGPath } from '../utils/trans.js';
import { MapControls } from '../utils/OrbitControls.js';
import { GLTFLoader } from '../utils/GLTFLoader.js';
import Stats from 'stats.js';

import './MapViewer.css';
import { ThreeDRotationSharp } from '@material-ui/icons';

const FIXED_HEIGHT = window.innerHeight;

const FLOOR_SCALE = 8;

const CAMERA_OFFSET_Y = 200;

const MODELS_PATH = 'models/';
const ARROW_MODEL_PATH = 'arrow.gltf';


const intl_messages = defineMessages({
  carinfo: {
    id: "mapviewer.carinfo",
    defaultMessage: "目的地: {carNum}  編號: {carPlate}"
  },
  sameFloor: {
    id: 'mapviewer.sameFloor',
    defaultMessage: "目前樓層: {currentFloor}，目的地位於本樓層",
  },
  diffFloor: {
    id: 'mapviewer.diffFloor',
    defaultMessage: "目前樓層: {currentFloor}，請搭乘電梯前往: {endFloor}",
  },
  betweenFloor: {
    id: "mapviewer.betweenfloor",
    defaultMessage: "樓層移動中..."
  }
})

const makeLabelCanvas = (baseWidth, size, name, fillColor) => {
  const borderSize = 2;
  const ctx = document.createElement('canvas').getContext('2d');
  const font = `${size}px bold STYuanti-TC`;
  ctx.font = font;
  // measure how long the name will be
  const textWidth = ctx.measureText(name).width;

  const doubleBorderSize = borderSize * 2;
  const width = textWidth + doubleBorderSize;
  const height = size + doubleBorderSize;
  ctx.canvas.width = width;
  ctx.canvas.height = height;

  // need to set font again after resizing canvas
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  ctx.fillStyle = fillColor;
  ctx.fillRect(0, 0, width, height);

  ctx.translate(width / 2, height / 2);
  ctx.fillStyle = 'black';
  ctx.fillText(name, 0, 0);

  return ctx.canvas;
};

function MapViewer(props) {
  const location = useLocation();
  const intl = useIntl();
  const APIKEY = useSelector(state => state.apikey);
  const BUILDING_ID = useSelector(state => state.buildingId);
  const FLOOR_HEIGHT = useSelector(state => state.floorHeight);
  const MAX_HEIGHT = useSelector(state => state.maxHeight);
  const FLOOR_TABLE = useSelector(state => state.floorTable);
  const FLOOR_LIST = useSelector(state => state.floorList);
  const injectProp = {
    intl: intl,
    apikey: APIKEY,
    building_id: BUILDING_ID,
    FLOOR_HEIGHT: FLOOR_HEIGHT,
    MAX_HEIGHT: MAX_HEIGHT,
    FLOOR_TABLE: FLOOR_TABLE,
    FLOOR_LIST: FLOOR_LIST,
  };
  return (
    <div id='threecontainer'>
      <THREEContainer location={location} {...injectProp} />
    </div>
  );
}

class THREEContainer extends Component {
  async componentDidMount() {
    /* THREE js Entrypoint */
    await this.loadResource();
    await this.loadObjects();
    this.sceneSetup();
    await this.addCustomSceneObjects();
    this.startAnimationLoop();
  }
  componentWillUnmount() {
    /* THREE js unmount */
    window.cancelAnimationFrame(this.requestID);
    this.controls.dispose();
  }
  constructor(props) {
    super(props);
    this.intl = props.intl;
    var pointData = props.location.state.pointData;
    this.floorList = [];
    this.floorIdList = [];
    this.floorDataList = [];
    this.floorPOIList = [];
    this.models = {};
    this.needUpdate = true;
    this.guiData = {
      //startX=800&startY=350&startZ=111&ndX=1150&endY=550&endZ=112
      apikey: props.location.state.apikey,
      startX: pointData.startX,
      startY: pointData.startY,
      startZ: pointData.startZ,
      startFloor: pointData.startFloor,
      endX: pointData.endX,
      endY: pointData.endY,
      endZ: pointData.endZ,
      endFloor: pointData.endFloor,
      pathCamera: false,
      replayPath: false,
      pathArrowList: [],
      applyPath: () => {
        this.refreshMap();
      },
      resetCamera: () => {
        this.controls.reset();
      },
    };
  }
  loadObjects = () => {
    /* load arrow obj */
    const loadArrowObj = () => {
      var loader = new GLTFLoader();
      loader.load('models/arrow/scene.gltf', gltf => {
        const root = gltf.scene;
        root.traverse(child => {
          if (child.isMesh) {
            this.models.arrow = {
              material: child.material,
              geometry: child.geometry,
            };
          }
        });
      });
    };
    loadArrowObj();
  };
  loadResource = () => {
    const resourceCode = undefined;
    const floorResourceURL =
      'https://stoparking.sto-tek.com/api/updateResource?apiKey=' +
      this.props.apikey +
      '&code=' +
      resourceCode;
    const POI_RESOURCE_PNG_URL = '/resource';
    const fetchResourceList = async () => {
      await fetch(floorResourceURL)
        .then(response => {
          response.json().then(res_json => {
            var resource_list = res_json.file;
            return;
          });
        })
        .catch(err => {
          console.error("fetch resource fail'");
          return;
        });
    };
    var result = fetchResourceList();
  };
  sceneSetup = () => {
    const width = this.el.clientWidth;
    const height = this.el.clientHeight;

    this.scene = new THREE.Scene();
    this.hudScene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      40,
      width / height,
      0.1,
      this.props.MAX_HEIGHT,
    );
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setSize(width, height);
    this.renderer.autoClear = false;
    this.el.appendChild(this.renderer.domElement);
    /* pathCamera and camera Helper */
    this.pathCamera = new THREE.PerspectiveCamera(80, width / height, 20, 20000);
    this.cameraTime = 0;
    this.cameraControl = {
      animeRunning: false,
      state: 'observe',
      pathCount: 0,
      pathList: [],
      spiralAngle: 0 / (Math.PI / 180),
      spiralRadius: 5,
      initRadius: 5,
      initObservePoint: {
        x: 0,
        y: 0,
        z: 0,
      },
      observeToStartLine: undefined,
    };
    this.pathCameraLine = undefined;
    this.arrowLine = undefined;

    /* Camera Control */
    this.controls = new MapControls(this.pathCamera, this.el);
    this.controls.screenSpacePanning = false;
    //this.controls.maxPolarAngle = Math.PI / 2 - Math.PI / 6;
    //this.controls.minPolarAngle = Math.PI / 4;
    this.controls.maxDistance = this.props.FLOOR_HEIGHT * 300;
    this.controls.minDistance = this.props.FLOOR_HEIGHT * 2;

    /* create HUD Scene */
    this.hudCamera = new THREE.OrthographicCamera(
      -width / 2,
      width / 2,
      height / 2,
      -height / 2,
      0,
      30,
    );
    this.HUD = {};
    this.HUD.hudCanvas = document.createElement('canvas');
    this.HUD.hudCanvas.width = width;
    this.HUD.hudCanvas.height = height;
    this.HUD.hudBitmap = this.HUD.hudCanvas.getContext('2d');
    /* create HUD Texture */
    this.HUD.hudTexture = new THREE.Texture(this.HUD.hudCanvas);
    this.HUD.hudTexture.minFilter = THREE.LinearFilter;
    this.HUD.hudTexture.needsUpdate = true;
    /* create HUD Material */
    var hudMaterial = new THREE.MeshBasicMaterial({
      map: this.HUD.hudTexture,
      side: THREE.DoubleSide,
    });
    hudMaterial.transparent = true;
    /* create plane to HUD scene */
    var hudGeometry = new THREE.PlaneBufferGeometry(width, height);
    this.HUD.hudPlane = new THREE.Mesh(hudGeometry, hudMaterial);
    this.hudScene.add(this.HUD.hudPlane);

    /* Create Stats to measure memory usage/render time */
    this.stats = new Stats();
    //this.el.appendChild(this.stats.dom)
  };
  addCustomSceneObjects = () => {
    /* our map object */
    this.floorGroup = new THREE.Group();
    this.pathGroup = new THREE.Group();
    this.scene.add(this.floorGroup);
    this.scene.add(this.pathGroup);

    /* light */
    const target = new THREE.Object3D();
    target.position.copy(this.camera.position);
    target.position.setZ(0);
    this.scene.add(target);
    const light = new THREE.SpotLight(0xffffff, 0.08, 0, Math.PI / 2, 1, 2);
    const envLight = new THREE.AmbientLight('#0c0c0c');
    light.position.set(0, 200000, 0);
    light.castShadow = true;
    light.target = target;
    this.scene.add(light);
    this.scene.add(envLight);

    /* My Object */
    this.refreshMap();
  };
  fetchData = () => {
    const getFloorJSON = async map_id => {
      if (map_id === null) return {};
      const url =
        'https://map.sto-tek.com/proxy/svg/?apikey=' +
        this.props.apikey +
        '&id=' +
        map_id +
        '&raw_data';
      /* Use Proxy to fetch from https://map.sto-tek.com/ */
      await fetch(url).then(response => {
        return response.json().then(value => {
          var map_id = parseInt(value.svg_id, 10);
          if (this.floorIdList.indexOf(map_id) !== -1) return;
          else {
            this.floorIdList.push(map_id);
            this.floorList.push(value);
            return getFloorPOI(map_id);
          }
        });
      });
    };
    const getFloorPOI = async map_id => {
      if (map_id === null) return {};
      //var floor_id = map_id;
      var floor_id = 'all'; /* api not support floor */
      var building_id = this.props.building_id; /* Taipei City Hall is 63 */
      const url =
        'https://stoparking.sto-tek.com/api/getpois?apiKey=' +
        this.props.apikey +
        '&building=' +
        building_id +
        '&floor=' +
        floor_id;
      await fetch(url).then(response => {
        return response.json().then(value => {
          var floor_list = value.info;
          var current_floor = floor_list.find(floor => {
            return parseInt(floor.MapID, 10) === map_id;
          });
          if (current_floor === undefined) {
            return;
          }
          var floor_poi_list = current_floor.PoiDetail;
          floor_poi_list.forEach(poi => {
            var POI = {
              pos_x: poi.PosX,
              pos_y: poi.PosY,
              map_id: map_id,
              name: poi.name,
              name_english: poi.nameEnglish,
              category: poi.Category,
              icon_id: poi.iconID,
              department_name: poi.departmentName,
              color: poi.colorCode /* for POI Label */,
            };
            this.floorPOIList.push(POI);
          });
          return;
        });
      });
    };
    /* CleanUp floor */
    this.removeFloor();
    /* Get start/end floor map */
    var floorPair = [this.guiData.startZ, this.guiData.endZ];
    const pList = floorPair.map(map_id => {
      return getFloorJSON(map_id);
    });
    return pList;
  };
  refreshMap = () => {
    this.removePathObj();
    const pList = this.fetchData();

    /* After fetch complete */
    Promise.all(pList).then(async () => {
      await this.addFloor();
      await this.addPathSphere();
      this.createObservePosition();
      this.setCameraToObserve();
    });
  };
  createObservePosition = () => {
    var maxCenter = {
      x: 0,
      y: 0,
    };
    this.floorDataList.forEach(floorData => {
      var center = floorData.center;
      if (center.x > maxCenter.x) {
        maxCenter.x = center.x;
      }
      if (center.y > maxCenter.y) {
        maxCenter.y = center.y;
      }
    });
    /* convert xy-coordinate to xz-coordinate */
    var floorHeight = Math.max(maxCenter.x, maxCenter.y) / 2;
    var maxHeight =
      Math.max(this.floorDataList.length - 1, 1) *
      floorHeight;
    var viewHeight = maxHeight * 1.66;
    var startFloor = this.floorDataList.find(floorData => {
      return parseInt(floorData.map_id) === parseInt(this.guiData.startZ);
    });
    var startPoint = new THREE.Vector3(
      parseInt(this.guiData.startX) - startFloor.center.x,
      startFloor.floor_offset,
      parseInt(this.guiData.startY) - startFloor.center.y,
    );
    this.cameraControl.initObservePoint = {
      x: -0.7 * maxCenter.x,
      y: viewHeight,
      z: -0.7 * maxCenter.y,
    };
    var linePositions = [this.cameraControl.initObservePoint, startPoint];
    var curve = new THREE.CatmullRomCurve3(linePositions);
    curve.curveType = 'catmullrom';
    this.cameraControl.observeToStartLine = curve; /* splinecurve */
  };
  addFloor = () => {
    /* need floor info (z index) */
    const createFloor = floorPath => {
      var center = floorPath.center;
      var paths = floorPath.paths;
      var depths = floorPath.depths;
      var svg_id = floorPath.svg_id;
      var map_id = parseInt(svg_id, 10);
      var index = this.floorIdList.indexOf(map_id);
      var floor_height = Math.floor((center.x + center.y) / 30);
      if (floor_height < 15) {
        floor_height = 15;
      }
      else if (floor_height > 100) {
        floor_height = 100;
      }
      var floor_offset = index * floor_height * FLOOR_SCALE;
      this.floorDataList[index] = {
        map_id: map_id,
        center: center,
        floor_height: floor_height,
        floor_offset: floor_offset,
      };
      var colors = floorPath.colors;
      for (var i = 0; i < paths.length; i++) {
        var path = paths[i],
          color = colors[i],
          depth = depths[i];
        this.pathToShape({ path, color, depth, floor_height, floor_offset, center });
      }
    };
    const createPOIMark = floor => {
      var center = floor.center;
      var map_id = parseInt(floor.svg_id, 10);
      var index = this.floorIdList.indexOf(map_id);
      var floor_offset = this.floorDataList[index].floor_offset;
      var floor_height = this.floorDataList[index].floor_height;
      var labelScale = Math.floor((center.x + center.y) / 50) * 1;
      const MARK_OFFSET = 3;
      /* mark color/material */
      var color = new THREE.Color('rgba(50, 210, 210, 0.2)');
      var mark_geometry = new THREE.SphereBufferGeometry(0.3, 32, 32);
      var mark_material = new THREE.MeshBasicMaterial({
        color: color,
        opacity: 0,
      });
      /* filter this floor POI and add Obj */
      var floor_poi = this.floorPOIList.filter(poi => {
        return parseInt(poi.map_id, 10) === map_id;
      });
      floor_poi.forEach(poi => {
        var cone = new THREE.Mesh(mark_geometry, mark_material);
        cone.position.x = poi.pos_x - center.x;
        cone.position.y = floor_offset - MARK_OFFSET;
        cone.position.z = poi.pos_y - center.y;
        cone.rotation.x = Math.PI / 2; /* 90 */

        /* ThreeJS Sprite Canvas Label */
        var canvas = makeLabelCanvas(48, labelScale, poi.name, poi.color);
        var texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        var labelMaterial = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
        });
        var labelBaseScale = 0.2;
        var label = new THREE.Sprite(labelMaterial);
        label.position.x = poi.pos_x - center.x;
        label.position.z = poi.pos_y - center.y;
        label.position.y = floor_offset + 0.48 * floor_height;
        label.scale.x = canvas.width * labelBaseScale;
        label.scale.y = canvas.height * labelBaseScale;
        //cone.add(label);
        //this.floorGroup.add(cone);
        this.floorGroup.add(label);
      });
    };
    /* sort floor */
    this.floorList.sort((a, b) => {
      let a_index = this.props.FLOOR_LIST.indexOf(parseInt(a.svg_id));
      let b_index = this.props.FLOOR_LIST.indexOf(parseInt(b.svg_id));

      return b_index - a_index;
    });
    this.floorIdList.sort((a, b) => {
      let a_index = this.props.FLOOR_LIST.indexOf(a);
      let b_index = this.props.FLOOR_LIST.indexOf(b);
      return b_index - a_index;
    });
    this.floorList.forEach(floor => {
      createFloor(floor);
      /* create POI landmark on floor */
      createPOIMark(floor);
    });
  };
  removeFloor = () => {
    var disposeList = [];
    if (this.floorGroup.children) {
      for (var i = 0; i < this.floorGroup.children.length; i++) {
        if (this.floorGroup.children[i].type === 'Mesh') {
          disposeList.push(this.floorGroup.children[i]);
        }
      }
    }
    disposeList.forEach(obj => {
      obj.geometry.dispose();
      obj.material.dispose();
      this.floorGroup.remove(obj);
      this.scene.remove(obj);
    });
    this.floorList = [];
    this.floorIdList = [];
    this.floorDataList = [];
  };
  removePathObj = () => {
    var disposeList = [];
    if (this.pathGroup.children) {
      for (var i = 0; i < this.pathGroup.children.length; i++) {
        if (this.pathGroup.children[i].type === 'Mesh') {
          disposeList.push(this.pathGroup.children[i]);
        }
      }
    }
    disposeList.forEach(obj => {
      obj.geometry.dispose();
      obj.material.dispose();
      this.pathGroup.remove(obj);
      this.scene.remove(obj);
    });
  };
  convertPointOffset = point => {
    var { x, y, map_id } = point;
    if (typeof map_id !== Number) {
      map_id = parseInt(map_id, 10);
    }

    var map_index = this.floorIdList.indexOf(map_id);
    var floor_offset = this.floorDataList[map_index].floor_offset;
    var center = this.floorDataList[map_index].center;

    var offset_x = x - center.x;
    var offset_y = floor_offset;
    var offset_z = y - center.y;

    return { offset_x, offset_y, offset_z };
  };
  addPathSphere = async () => {
    const getPath = () => {
      const startX = this.guiData.startX,
        startY = this.guiData.startY,
        startZ = this.guiData.startZ;
      const endX = this.guiData.endX,
        endY = this.guiData.endY,
        endZ = this.guiData.endZ;
      const url =
        'https://analytics.sto-tek.com/_nodered/api/pathPlan?apiKey=' +
        this.props.apikey +
        '&startX=' +
        startX +
        '&startY=' +
        startY +
        '&startZ=' +
        startZ +
        '&endX=' +
        endX +
        '&endY=' +
        endY +
        '&endZ=' +
        endZ +
        '';

      /* Use Proxy to fetch from https://stoparking.sto-tek.com */
      return fetch(url);
    };
    const createPathObject = naviPath => {
      const directionAngle = {
        NORTH: -Math.PI / 2,
        EAST: Math.PI,
        SOUTH: Math.PI / 2,
        WEST: 0,
        UP: 'UP',
        DOWN: 'DOWN',
      };
      const pathList = naviPath.path;
      var upDownList = [];
      var horizonPathList = [];
      var verticalPathList = [];
      var cameraPathList = [];
      var shape = new THREE.BufferGeometry()
        .copy(this.models.arrow.geometry)
        .scale(0.2, 0.2, 0.2);
      var start_color = new THREE.Color('rgb(220, 100, 100)');
      var path_color = new THREE.Color('rgb(0, 200, 200)');
      var end_color = new THREE.Color('rgb(190, 30, 170)');
      /* add start/end point to path */
      var startPoint = {
        x: this.guiData.startX,
        y: this.guiData.startY,
        z: parseInt(this.guiData.startZ, 10),
      };
      var endPoint = {
        x: this.guiData.endX,
        y: this.guiData.endY,
        z: parseInt(this.guiData.endZ, 10),
      };
      pathList.unshift(startPoint);
      pathList.push(endPoint);

      pathList.forEach((point, index, _arr) => {
        let direction = undefined;
        let floor_z = point.z;
        let floor_index = this.floorIdList.indexOf(floor_z);
        var floor_height = this.floorDataList[floor_index].floor_height;
        point.floor_index = floor_index;
        point.floor_offset =
          floor_index * floor_height * FLOOR_SCALE;
        if (index === 0) {
          point.color = start_color;
        } else if (index < _arr.length - 2) {
          /* not end */
          point.color = path_color;
          let _pre = _arr[index - 1];
          /* decide direction */
          let _dx = point.x - _pre.x;
          let _dy = point.y - _pre.y;
          let _dz =
            this.props.FLOOR_LIST.indexOf(point.z) -
            this.props.FLOOR_LIST.indexOf(_pre.z);
          if (_dz === 0) {
            if (_dx === 0) {
              /* EAST or WEST */
              if (_dy > 0) {
                point.direction = directionAngle.EAST;
              } else {
                point.direction = directionAngle.WEST;
              }
            } else if (_dy === 0) {
              /* NORTH or SOUTH */
              if (_dx > 0) {
                point.direction = directionAngle.NORTH;
              } else {
                point.direction = directionAngle.SOUTH;
              }
            } else {
              /* can not decide direction */
              /* follow last direction */
              point.direction = _arr[index - 1].direction;
            }
          } else {
            /* upDown Flag will reverse offset */
            var upDownFlag = -1;
            var upDownDirection = directionAngle.UP;
            /* this point don't show on screen, set direction&color to undefined */
            if (_dz > 0) {
              point.direction = undefined;
              point.color = undefined;
              upDownDirection = directionAngle.UP;
            } else if (_dz < 0) {
              point.direction = undefined;
              point.color = undefined;
              upDownDirection = directionAngle.DOWN;
              upDownFlag = 1;
            }
            /* generate up/down arrow point array */
            var project_pre = _pre;
            for (var i = 0; i < 10; i++) {
              var insert_point = _.cloneDeep(_pre);
              insert_point.floor_offset =
                _pre.floor_offset +
                upDownFlag *
                this.props.FLOOR_HEIGHT *
                ((i + 1) / 11) *
                FLOOR_SCALE;
              insert_point.color = path_color;
              insert_point.direction = upDownDirection;
              upDownList.push(insert_point);
            }
          }
        } else {
          /* last point */
          /* follow previous direction */
          //point.direction = _arr[index-1].direction
          point.color = end_color;
        }
      });
      const _convertArrowDirection = (mesh, direction) => {
        /* move then rotate */
        /* arrow pre-rotate */
        mesh.rotateZ(Math.PI / 2);

        switch (direction) {
          case directionAngle.NORTH:
            mesh.rotateX(direction);
            break;
          case directionAngle.SOUTH:
            mesh.rotateX(direction);
            break;
          case directionAngle.EAST:
            mesh.rotateX(direction);
            break;
          case directionAngle.WEST:
            mesh.rotateX(direction);
            break;
          case directionAngle.DOWN:
            break;
          case directionAngle.UP:
            mesh.rotateX(Math.PI);
            mesh.translateY(-30);
            break;
          default:
            mesh.rotateY(Math.PI / 2);
            break;
        }
      };
      pathList.forEach((point, index, array) => {
        /* get point position */
        var floor_index = point.floor_index;
        var center = this.floorDataList[floor_index].center;
        var floor_height = this.floorDataList[floor_index].floor_height;
        var offset_x = point.x - this.floorList[floor_index].center.x;
        var offset_z = point.y - this.floorList[floor_index].center.y;
        var offset_y = point.floor_offset;
        /* draw sphere */
        var material = new THREE.MeshBasicMaterial({
          color: point.color,
          side: THREE.DoubleSide,
        });
        var mesh = new THREE.Mesh(shape, material);
        /* rotate to direction */
        mesh.position.set(offset_x, offset_y + floor_height * 0.2, offset_z);
        horizonPathList.push({ offset_x, offset_y, offset_z });
        _convertArrowDirection(mesh, point.direction);
        if (point.direction !== undefined || point.color !== undefined) {
          if (index < array.length - 1) {
            this.pathGroup.add(mesh);
          }
        }
      });
      upDownList.forEach((point, index) => {
        var floor_index = point.floor_index;
        var offset_x = point.x - this.floorList[floor_index].center.x;
        var offset_z = point.y - this.floorList[floor_index].center.y;
        var offset_y = point.floor_offset;
        /* put up/down path into vertial path line */
        verticalPathList.push({ offset_x, offset_y, offset_z });
      });
      /* split horizonPath, find floor change index */
      const ifDifferent = point =>
        point.offset_y !== horizonPathList[0].offset_y;
      var changeIndex = horizonPathList.findIndex(ifDifferent);
      var frontFloor = horizonPathList;
      var backFloor = [];
      if (changeIndex !== -1) {
        frontFloor = horizonPathList.slice(0, changeIndex);
        backFloor = horizonPathList.slice(changeIndex);
      }
      /* vertical path */
      var verticalMovePath = [];
      verticalPathList.map((point, index, source) => {
        var start_y = source[0].offset_y;
        var end_y = source[source.length - 1].offset_y;
        var middle_y = (start_y + end_y) / 2;
        var xz_offset = Math.abs(point.offset_y - middle_y) * 1;
        xz_offset = index < source.length / 2 ? xz_offset : -xz_offset;
        var cameraPosition = {
          offset_x: point.offset_x - xz_offset,
          offset_y: point.offset_y,
          offset_z: point.offset_z - xz_offset,
        };
        if (index !== 0) {
          cameraPosition = {
            offset_x: verticalMovePath[index - 1].offset_x - xz_offset,
            offset_y: point.offset_y,
            offset_z: verticalMovePath[index - 1].offset_z - xz_offset,
          };
        }
        verticalMovePath.push(cameraPosition);
      });
      cameraPathList = frontFloor.concat(verticalMovePath, backFloor);
      var cameraTargetList = frontFloor.concat(verticalPathList, backFloor);
      //cameraPathList = horizonPathList;
      /* want to move camera to start point */
      this.createCameraPath(cameraPathList, cameraTargetList);
    };
    this.removePathObj();
    await getPath().then(response => {
      response.json().then(pathArr => {
        this.guiData.pathArray = pathArr.path;
        return createPathObject(pathArr);
      });
    });
  };
  createCameraPath = (cameraPath, targetPath) => {
    /* create Vector3 array for spline */
    var vector3List = [];
    var arrow3List = [];
    cameraPath.forEach(element => {
      var _x = element.offset_x;
      var _y = element.offset_y;
      var _z = element.offset_z;
      vector3List.push(new THREE.Vector3(_x, _y + CAMERA_OFFSET_Y, _z));
    });
    targetPath.forEach(element => {
      var _x = element.offset_x;
      var _y = element.offset_y;
      var _z = element.offset_z;
      arrow3List.push(new THREE.Vector3(_x, _y, _z));
    });
    /* create path CaumullRomCurve3 */
    this.pathCameraLine = new THREE.CatmullRomCurve3(vector3List);
    this.arrowLine = new THREE.CatmullRomCurve3(arrow3List);
    /* create Line to show at scene */
    this.guiData.pathArrowList = cameraPath.slice();

    /* DEBUG */
  };
  startNaviPath = () => {
    this.setCameraToObserve();
    this.cameraControl.state = 'start';
  };
  setCameraToObserve = () => {
    var center = this.floorDataList[0].center; /* lowest floor */
    var observePoint = this.cameraControl.observeToStartLine.getPoint(0);
    this.pathCamera.position.set(observePoint.x, observePoint.y, observePoint.z);
    this.pathCamera.lookAt(0, 0, 0);
    this.pathCamera.updateProjectionMatrix();
    this.cameraControl.state = 'observe';
    this.cameraTime = 0;
  };
  moveCameraToStart = () => {
    var startPoint = this.pathCameraLine.getPoint(0);
    var cameraTime = this.cameraTime;
    const maxIndex = 200;
    const timeStep = 2;
    var cameraPosIndex = cameraTime / maxIndex;
    var cameraPos = this.cameraControl.observeToStartLine.getPoint(
      cameraPosIndex,
    );
    var cameraRot = this.cameraControl.observeToStartLine.getTangent(
      cameraPosIndex,
    );

    this.pathCamera.position.x = cameraPos.x;
    this.pathCamera.position.y = cameraPos.y;
    this.pathCamera.position.z = cameraPos.z;
    this.pathCamera.lookAt(new THREE.Vector3(startPoint.x, 0, startPoint.z));
    this.pathCamera.updateProjectionMatrix();

    if (this.cameraTime >= maxIndex * 0.9) {
      this.cameraTime = 0;
      this.cameraControl.state = 'path';
    }
    this.cameraTime = this.cameraTime + timeStep;
  };
  movePathCamera = () => {
    /* move pathCamera to walk through */
    const scale = 35;
    const timeStep = 2; //4
    var maxIndex = this.guiData.pathArrowList.length;
    /*reset*/
    if (this.cameraTime > maxIndex * scale * 1.2) {
      this.cameraTime = 0;
      this.setCameraToObserve();
      return;
    }
    var cameraTime = this.cameraTime;
    if (this.cameraTime > maxIndex * scale * 0.95) {
      cameraTime = maxIndex * scale * 0.95;
    }
    var cameraPosIndex = cameraTime / (maxIndex * scale);
    var cameraPos = this.pathCameraLine.getPoint(cameraPosIndex);
    var cameraRot = this.pathCameraLine.getTangent(cameraPosIndex);

    /* set pathCamera position/rotation */
    this.pathCamera.position.x = cameraPos.x;
    this.pathCamera.position.y = cameraPos.y;
    this.pathCamera.position.z = cameraPos.z;

    /* lookAt path Arrow */
    var arrowTime = this.cameraTime + timeStep * 10;
    if (arrowTime > maxIndex * scale) {
      arrowTime = maxIndex * scale;
    }
    var arrowPoint = this.arrowLine.getPoint(arrowTime / (maxIndex * scale));
    this.pathCamera.lookAt(arrowPoint);
    

    /* The Show Must Go On */
    this.cameraTime = this.cameraTime + timeStep;
  };
  updateHUDCanvas = () => {
    const carInfo = this.props.location.state.carInfo;
    const ctx = this.HUD.hudBitmap;
    const width = this.HUD.hudCanvas.width;
    const height = this.HUD.hudCanvas.height;
    const center = { x: width / 2, y: height / 2 };
    var lineHeight = width / 4 / 3; /* 動態計算文字行高 */
    var baselinePosition = 10; /* 起始 offset */

    const cameraHeight = Math.floor(this.pathCamera.position.y);

    /* change Y to floor name here */
    const measureFloor = height => {
      var return_text = this.intl.formatMessage(intl_messages.betweenFloor);
      var currentFloor = this.floorDataList.find(floorData => {
        var floor_distance = height - floorData.floor_offset;
        return floor_distance === CAMERA_OFFSET_Y;
      });
      if (currentFloor) {
        var floorTarget = this.props.FLOOR_TABLE.find(floor => {
          return parseInt(floor.id) === parseInt(currentFloor.map_id);
        });
        if (floorTarget !== undefined) {
          return_text = floorTarget.name;
        }
      } else if (this.cameraControl.state === 'observe') {
        var floorTarget = this.props.FLOOR_TABLE.find(floor => {
          return parseInt(floor.id) === parseInt(this.guiData.startZ);
        });
        if (floorTarget !== undefined) {
          return_text = floorTarget.name;
        }
      }
      return return_text;
    };
    var drawText = [];

    /* create GOAL text */
    drawText.push(
      this.intl.formatMessage(intl_messages.carinfo, { carNum: carInfo.name, carPlate: carInfo.number })
      // this.intl.formatMessage(intl_messages.carinfo, { carNum: carInfo.ParkingNumber, carPlate: carInfo.LicensePlate })
    );
    var currentFloor = measureFloor(cameraHeight);
    var floorText = '';
    if (!currentFloor) {
      floorText = this.intl.formatMessage(intl_messages.betweenFloor);
    } else {
      floorText = this.intl.formatMessage(intl_messages.betweenFloor);
    }
    if (currentFloor !== this.guiData.endFloor) {
      floorText = this.intl.formatMessage(intl_messages.diffFloor, { currentFloor, endFloor: this.guiData.endFloor });
    } else {
      floorText = this.intl.formatMessage(intl_messages.sameFloor, { currentFloor });
    }
    /* currentFloor(現在樓層) this.guiData.endFloor (目標樓層) BETWEEN_FLOOR(移動中) */
    drawText.push(floorText);

    var txtWidth = ctx.measureText(drawText).width;
    this.HUD.hudBitmap.clearRect(0, 0, width, height);
    this.HUD.hudBitmap.fillStyle = 'rgba(222, 222, 222, 1)';
    /* full width */
    this.HUD.hudBitmap.fillRect(
      0,
      0,
      width,
      //lineHeight * drawText.length,
      width / 4,
    ); /* left, top, right, bottom */
    /* change font-size with width(RWD) */
    var fontSize = '48px';
    if (width < 900) {
      fontSize = '28px';
    }
    if (width < 600) {
      fontSize = '16px';
    }
    this.HUD.hudBitmap.font = 'Normal ' + fontSize + ' STYuanti-TC';
    this.HUD.hudBitmap.textAlign = 'center';
    this.HUD.hudBitmap.fillStyle = 'rgba(51, 51, 51, 1)';
    this.HUD.hudBitmap.textBaseline = 'top'; /* 從頂端為基準線 */
    drawText.forEach(text => {
      this.HUD.hudBitmap.fillText(text, width / 2, baselinePosition);
      baselinePosition += lineHeight;
    });
    this.HUD.hudTexture.needsUpdate = true;

    /* update height record */
    this.HUD.prevHeight = cameraHeight;
  };
  setCameraChoose = e => {
    this.guiData.pathCamera = e.target.checked;
  };
  setReplayPath = e => {
    this.guiData.replayPath = e.target.checked;
  };
  /* pathToShape: for create floor model */
  pathToShape = ({ path, color, depth, floor_height, floor_offset, center }) => {
    var shapesPath = transformSVGPath(path).toShapes(true);
    var floor_depth = depth * floor_height;
    var geometrySetting = {
      depth: floor_depth,
      bevelEnable: false,
      bevelSize: 0,
      bevelSegment: 1,
      bevelThickness: 1,
    };
    var material = new THREE.MeshLambertMaterial({
      color: color,
      emissive: color,
      wireframe: false,
      transparent: false,
      opacity: 1.0,
      side: THREE.DoubleSide,
    });
    shapesPath.forEach(shape => {
      var geometry = new THREE.ExtrudeBufferGeometry(shape, geometrySetting);
      var mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.x = Math.PI / 2;
      mesh.translateZ(-floor_depth - floor_offset);
      mesh.translateX(-center.x);
      mesh.translateY(-center.y);
      mesh.receiveShadow = false;
      this.floorGroup.add(mesh);
    });
  };
  startAnimationLoop = () => {
    //var useCamera = this.guiData.pathCamera ? this.pathCamera : this.camera;
    var useCamera = this.pathCamera;
    /* loop animation */
    if (this.pathCameraLine !== undefined) {
      /* this section is camera moving */
      /* NEED: a path-traverse camera moving, to floor change or goal point */
      if (this.cameraControl.state === 'observe') {
        if (this.needUpdate) {
          this.updateHUDCanvas();
        }
        this.needUpdate = false;
      }
      if (this.cameraControl.state === 'start') {
        this.moveCameraToStart();
        this.needUpdate = true;
      }
      if (this.cameraControl.state === 'path') {
        /* update HUD info */
        this.updateHUDCanvas();
        /* update Camera */
        this.movePathCamera();
        this.needUpdate = true;
      }
      /* NEED: if cross-floor, make a jump-out and move to next floor start point */
    }
    this.stats.begin();
    /* render 3d scene */
    this.renderer.render(this.scene, useCamera);
    /* render HUD screen */
    this.renderer.render(this.hudScene, this.hudCamera);
    this.stats.end();
    if (this.cameraControl.state === 'observe') {
      this.controls.update();
    }
    this.requestID = window.requestAnimationFrame(this.startAnimationLoop);
  };
  render() {
    return (
      <div id="mapviewer">
        <div
          id="threeview"
          ref={ref => (this.el = ref)}
        />
        <div className="configArea">
          <img src="img/btn-play.png" alt="play" onClick={this.startNaviPath} />
        </div>
      </div>
    );
  }
}

export default MapViewer;
